import type { CompRef } from '@wix/document-services-types';
import type { EditorReactComponent } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import {
  DISPLAY_GROUPS,
  type DisplayFilter,
  type EditorElement,
  type ElementItem,
  type InlineElement,
  type RefElement,
} from '@wix/component-protocol/schema';
import {
  EditorReactComponentExtensionType,
  IManifestStoreEntity,
  ManifestComponentExtensionType,
  ManifestStore,
} from '../stores';
import {
  IVirtualBuilder,
  IVirtualManifest,
  IVirtualManifestItemMapping,
  IVirtualManifestMappings,
  REDUCIBLE_KEYS,
  ReducibleKey,
  VirtualManifestElementType,
} from './types';
import {
  StandaloneBuilder,
  UNEXISTING_COMPONENT_PREFIX,
} from './StandaloneBuilder';

import {
  DependencyToken,
  IDependencyAPI,
  DependenciesContributor,
} from '../dependencies';

import { ManifestServiceFlags } from '../ManifestServiceFlags';
import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export type ManifestPropertyFilter = (
  manifestProp: ReducibleKey,
  key: string,
  definition: any,
) => boolean;

export interface IVirtualManifestBuilderState {
  virtualManifests: IVirtualManifest[];
  manifests: {
    editorPointer: EditorPointer<CompRef>;
    store: IManifestStoreEntity<EditorReactComponentExtensionType>;
  }[];

  filters?: ManifestPropertyFilter;
}

export abstract class AbstractVirtualManifestBuilder
  implements IVirtualBuilder
{
  public readonly DependencyToken = DependencyToken;

  protected state: IVirtualManifestBuilderState = {
    virtualManifests: [],
    manifests: [],
  };

  contextAPIContributor = new DependenciesContributor();

  constructor(
    protected readonly manifestStore: ManifestStore,
    protected readonly dependenciesContributor: DependenciesContributor,
    protected readonly editorPointerAPI: EditorPointerServiceAPI,
    protected readonly manifestServiceFlags: ManifestServiceFlags,
  ) {}

  public extend<T extends AbstractVirtualManifestBuilder>(
    Builder: new (
      manifestStore: typeof this.manifestStore,
      dependenciesContributor: typeof this.dependenciesContributor,
      editorPointerAPI: typeof this.editorPointerAPI,
      manifestServiceFlags: typeof this.manifestServiceFlags,
    ) => T,
  ): T {
    return new Builder(
      this.manifestStore,
      this.dependenciesContributor,
      this.editorPointerAPI,
      this.manifestServiceFlags,
    );
  }

  abstract reduceManifestEntity<TManifestKey extends ReducibleKey>(
    manifestProp: TManifestKey,
    left: {
      value: EditorElement[TManifestKey] | undefined;
    },
    right: {
      value: EditorElement[TManifestKey] | undefined;
      mappings: Record<string, IVirtualManifestItemMapping[]>;
    },
    mappings: Record<string, IVirtualManifestItemMapping[]>,
  ): {
    value: EditorElement[TManifestKey];
    mappings: Record<string, IVirtualManifestItemMapping[]>;
  };

  public overrideDependencyMethod<TToken extends DependencyToken>(
    token: TToken,
    cb: (
      api: Extract<IDependencyAPI, { type: TToken }>['api'],
    ) => Extract<IDependencyAPI, { type: TToken }>['api'],
  ) {
    const systemAPI = this.dependenciesContributor.get<TToken>(token);

    /**
     * TODO: fix any
     */
    const api = cb(systemAPI as any);
    this.contextAPIContributor.set<TToken>(token, api as any);

    return this;
  }

  public withFilters(filter: ManifestPropertyFilter) {
    this.state.filters = filter;

    return this;
  }

  public withoutComponents() {
    /**
     * usually we should not pass "this" as arguments,
     * but we need to cover this case for backward-capability
     * ideally standalone builder should be implemented as a strategy
     */
    return new StandaloneBuilder(this);
  }

  public withManifest(
    editorPointer: EditorPointer<CompRef>,
    /**
     * NOTE: actually editorPointer may ref to an inner element,
     * therefore, manifest is not exactly the EditorReactComponent from the type perspective
     * more correct type is
     * {
     *   editorElement: EditorElement | InlineElement
     * }
     */
    manifest: EditorReactComponent,
    componentMetadata?: {
      componentType?: string;
      extensionType?: EditorReactComponentExtensionType;
    },
  ) {
    const isFakeComponent = editorPointer.asUniqueKey.includes(
      UNEXISTING_COMPONENT_PREFIX,
    );

    const storedManifest = isFakeComponent
      ? null
      : this.manifestStore.getStoredRootManifest(editorPointer);

    if (isFakeComponent || !storedManifest) {
      this.state.manifests.push({
        editorPointer,
        store: {
          extensionType:
            componentMetadata?.extensionType ??
            (ManifestComponentExtensionType.UNKNOWN as EditorReactComponentExtensionType),
          componentType: componentMetadata?.componentType ?? 'UNKNOWN',
          metadata: null,
          manifest,
        },
      });
    } else {
      const componentType =
        manifest.type ??
        this.manifestStore.getComponentTypeByEditorPointer(editorPointer) ??
        'UNKNOWN';

      this.state.manifests.push({
        editorPointer,
        store: {
          extensionType: storedManifest.extensionType,
          metadata: storedManifest.metadata,
          componentType,
          manifest,
        },
      });
    }

    return this;
  }

  public withEditorPointer(editorPointer: EditorPointer<CompRef>) {
    const resolvedEditorElement =
      this.manifestStore.getResolvedEditorElement(editorPointer);

    if (resolvedEditorElement) {
      this.withEditorElement(editorPointer, resolvedEditorElement);
    } else {
      this.withEditorElement(editorPointer, {});
    }

    return this;
  }

  public withEditorElement(
    editorPointer: EditorPointer<CompRef>,
    editorElement: EditorElement | InlineElement,
    componentMetadata?: {
      componentType?: string;
      extensionType?: EditorReactComponentExtensionType;
    },
  ) {
    return this.withManifest(
      editorPointer,
      { editorElement },
      componentMetadata,
    );
  }

  public withVirtualManifest(virtualManifest: IVirtualManifest) {
    this.state.virtualManifests.push(virtualManifest);
    return this;
  }

  protected reduceDisplayFilters(
    manifests: IVirtualManifestBuilderState['manifests'],
  ): EditorElement['displayFilters'] {
    const displayFiltersKeys = [
      'elements',
      'style',
      'data',
      'customActions',
      'actions',
      'cssProperties',
      'cssCustomProperties',
      'states',
      'presets',
    ] as const;

    return displayFiltersKeys.reduce((acc, key) => {
      const displayFilters: any = manifests
        .map(
          (x) =>
            x.store.manifest.editorElement?.displayFilters?.[
              // TODO: typecast should be removed after updating @wix/component-protocol to >= 1.154.0
              key as keyof EditorElement['displayFilters']
            ],
        )
        .filter((x) => !!x);

      const mergedDisplayFilter = displayFilters.reduce(
        // TODO: type annotations will be inferred after updating @wix/component-protocol to >= 1.154.0
        (_acc: DisplayFilter, displayFilter: DisplayFilter) => {
          return {
            hide: [...new Set([..._acc.hide!, ...(displayFilter?.hide ?? [])])],
            show: [...new Set([..._acc.show!, ...(displayFilter?.show ?? [])])],
          };
        },
        {
          hide: [],
          show: [],
        },
      );

      if (
        !mergedDisplayFilter.hide?.length &&
        !mergedDisplayFilter.show?.length
      ) {
        return acc;
      }

      return {
        ...acc,
        [key]: mergedDisplayFilter,
      };
    }, {});
  }

  protected reduceDisplayGroups(
    manifests: IVirtualManifestBuilderState['manifests'],
  ): EditorElement['displayGroups'] {
    return manifests.reduce((acc, { store }) => {
      return {
        ...acc,
        ...store.manifest.editorElement?.displayGroups,
      };
    }, {});
  }

  protected reduceDisplayName(
    manifests: IVirtualManifestBuilderState['manifests'],
  ): EditorElement['displayName'] | InlineElement['displayName'] | undefined {
    if (manifests.length > 1) {
      return undefined;
    }

    return manifests[0].store.manifest?.editorElement?.displayName;
  }

  protected reduceVisibleState(
    manifests: IVirtualManifestBuilderState['manifests'],
  ): RefElement['visibleState'] {
    if (manifests.length > 1) {
      return undefined;
    }

    /**
     * TODO: better support editorElement/inlineElement/RefElement
     * in terms of builders, all manifests are under the "editorElement"
     */
    // @ts-expect-error visibleState is available only for inlineElement or refElement
    return manifests[0].store.manifest?.editorElement?.visibleState;
  }

  protected reduceLayout(
    manifests: IVirtualManifestBuilderState['manifests'],
  ): NonNullable<EditorElement['layout']> {
    function pick<T extends keyof NonNullable<EditorElement['layout']>>(
      manifest: IVirtualManifestBuilderState['manifests'][number],
      key: T,
    ): NonNullable<EditorElement['layout']>[T] {
      return manifest.store.manifest.editorElement?.layout?.[key];
    }

    const layouts = manifests.map(
      (m) => m.store.manifest.editorElement?.layout ?? {},
    );

    return filterUndefined({
      disableStretching: getUniformValue(layouts, 'disableStretching', false),
      disablePositioning: getUniformValue(layouts, 'disablePositioning', false),
      disableRotation: getUniformValue(layouts, 'disableRotation', false),
      disableDuplication: getUniformValue(layouts, 'disableDuplication', false),
      contentFill:
        manifests.length === 1 ? pick(manifests[0], 'contentFill') : undefined,
      resizeDirection: getUniformValue(
        layouts,
        'resizeDirection',
        // @ts-expect-error ResizeDirection enum is not exposed from type package
        'UNKNOWN_ResizeDirection',
      ),
      contentResizeDirection: getUniformValue(
        layouts,
        'contentResizeDirection',
        // @ts-expect-error ContentResizeDirection enum is not exposed from type package
        'UNKNOWN_ContentResizeDirection',
      ),
    });
  }

  protected buildChildren(
    { store, editorPointer }: IVirtualManifestBuilderState['manifests'][0],
    parentComponentType: string,
    breadcrumbs: string[],
  ): IVirtualManifest['tree']['children'] {
    const elements =
      (store.manifest as EditorReactComponent).editorElement?.elements ||
      (store.manifest as ElementItem).inlineElement?.elements ||
      (store.manifest as ElementItem).refElement?.resolvedElement?.elements;

    if (!elements) {
      return [];
    }

    const orderedElementNames = getOrderedElementNames(
      elements,
      store.manifest.editorElement?.displayGroups,
    );

    return orderedElementNames
      .map((elementName) => {
        const element = elements[elementName];

        if (!element) {
          return null;
        }

        const _manifest =
          element.inlineElement || element.refElement?.resolvedElement;

        const isRefElement = !!element.refElement;
        /**
         * TODO: probably we need to check if child is visible within the current state
         * but we can't just filter it here, since it means we need to rebuild context
         * every time state is changed we need to rebuild tree
         */
        if (!_manifest) {
          return null;
        }

        const childEditorPointer =
          this.editorPointerAPI.buildChildEditorPointer(editorPointer, [
            elementName,
          ]);

        const context = this.reduceManifests([
          {
            editorPointer: childEditorPointer,
            store: {
              manifest: {
                editorElement: _manifest,
              },
              extensionType: store.extensionType,
              componentType: store.componentType,
              metadata: store.metadata,
            },
          },
        ]);

        const _breadcrumbs = [
          ...breadcrumbs,
          elementName,
          ...(isRefElement
            ? ['refElement', 'resolvedElement']
            : ['inlineElement']),
        ];

        const componentType = element.refElement?.type ?? parentComponentType;

        return {
          elementType: isRefElement
            ? VirtualManifestElementType.RefElement
            : VirtualManifestElementType.Inline,
          breadcrumbs: _breadcrumbs,
          componentType,
          manifest: {
            editorElement: context.manifest,
          },
          mappings: context.mappings,
          editorPointer: childEditorPointer,
          extensionType: store.extensionType,
          elementName,
          deprecated: element.deprecated === true,
          children: this.buildChildren(
            {
              editorPointer: childEditorPointer,
              store: {
                manifest: {
                  editorElement: _manifest,
                },
                extensionType: store.extensionType,
                componentType: store.componentType,
                metadata: store.metadata,
              },
            },
            componentType,
            [..._breadcrumbs, 'elements'],
          ),
        };
      })
      .filter((x) => !!x);
  }

  protected applyFilters<TManifestKey extends ReducibleKey>(
    manifestProp: TManifestKey,
    definition: EditorElement[TManifestKey] | undefined,
  ): EditorElement[TManifestKey] | undefined {
    if (!this.state.filters || !definition) {
      return definition;
    }

    const filteredDefinition = {};

    for (const key of Object.keys(definition ?? {})) {
      const definitionItem = (definition as any)[key];

      if (this.state.filters(manifestProp, key, definitionItem)) {
        (filteredDefinition as any)[key] = definitionItem;
      }
    }

    return filteredDefinition as EditorElement[TManifestKey];
  }

  #mergeVirtualManifests(
    context: {
      manifest: IVirtualManifest['tree']['manifest']['editorElement'];
      mappings: IVirtualManifest['tree']['mappings'];
    } | null,
    virtualManifests: IVirtualManifestBuilderState['virtualManifests'] = [],
  ): {
    manifest: IVirtualManifest['tree']['manifest']['editorElement'];
    mappings: IVirtualManifest['tree']['mappings'];
  } {
    const mappings: IVirtualManifestMappings = {
      ...(context?.mappings ?? {}),
    };

    const manifest: EditorElement = {
      ...(context?.manifest ?? {}),
    };

    virtualManifests.forEach((virtualManifest) => {
      REDUCIBLE_KEYS.forEach((key) => {
        const manifestEntity =
          virtualManifest.tree.manifest.editorElement?.[key];

        // Apply filters to the input value
        const filteredValue = this.applyFilters(key, manifestEntity);

        const result = this.reduceManifestEntity(
          key,
          { value: manifest[key] },
          {
            value: filteredValue,
            mappings: virtualManifest.tree.mappings[key] ?? {},
          },
          mappings[key] ?? {},
        );

        (manifest as any)[key] = result.value;
        mappings[key] = result.mappings;
      });
    });

    const _manifests: IVirtualManifestBuilderState['manifests'] = [
      ...(context
        ? [
            {
              editorPointer: null! as EditorPointer<CompRef>,
              store: {
                manifest: { editorElement: context.manifest },
                extensionType:
                  ManifestComponentExtensionType.UNKNOWN as EditorReactComponentExtensionType,
                componentType: 'UNKNOWN',
                metadata: null,
              },
            },
          ]
        : []),
      ...virtualManifests.map((virtualManifest) => ({
        editorPointer: null! as EditorPointer<CompRef>,
        store: {
          manifest: virtualManifest.tree.manifest,
          extensionType:
            ManifestComponentExtensionType.UNKNOWN as EditorReactComponentExtensionType,
          componentType: 'UNKNOWN',
          metadata: null,
        },
      })),
    ];

    // fill with non-reducible keys
    Object.assign(manifest, {
      layout: this.reduceLayout(_manifests),
      visibleState: this.reduceVisibleState(_manifests),
      displayFilters: this.reduceDisplayFilters(_manifests),
      displayGroups: this.reduceDisplayGroups(_manifests),
      displayName: this.reduceDisplayName(_manifests),
      behaviors: (manifest as any).behaviors ?? {},
    });

    return {
      manifest,
      mappings,
    };
  }

  protected reduceManifests(
    manifests: IVirtualManifestBuilderState['manifests'],
  ): {
    manifest: IVirtualManifest['tree']['manifest']['editorElement'];
    mappings: IVirtualManifest['tree']['mappings'];
  } {
    if (!manifests.length) {
      return {
        manifest: {},
        mappings: {},
      };
    }

    const reducedManifest: EditorElement = {};
    const mappings: IVirtualManifestMappings = {};

    REDUCIBLE_KEYS.forEach((key) => {
      mappings[key] = {};
    });

    manifests.forEach((manifest) => {
      REDUCIBLE_KEYS.forEach((key) => {
        const manifestEntity = manifest.store.manifest?.editorElement?.[key];

        // Apply filters to the input value
        const filteredValue = this.applyFilters(key, manifestEntity);

        const result = this.reduceManifestEntity(
          key,
          {
            value: reducedManifest[key],
          },
          {
            value: filteredValue,
            mappings: Object.keys(filteredValue ?? {}).reduce(
              (acc, k) => ({
                ...acc,
                [k]: [
                  {
                    runtimeKey: k,
                    originalKey: k,
                    editorPointer: manifest.editorPointer,
                    extensionType: manifest.store.extensionType,
                  },
                ],
              }),
              {} as Record<string, IVirtualManifestItemMapping[]>,
            ),
          },
          mappings?.[key] ?? {},
        );

        // safe type assertion: we know the key is valid for EditorElement
        (reducedManifest as any)[key] = result.value;
        mappings[key] = result.mappings;
      });
    });

    // fill with non-reducible keys
    Object.assign(reducedManifest, {
      layout: this.reduceLayout(manifests),
      visibleState: this.reduceVisibleState(manifests),
      displayFilters: this.reduceDisplayFilters(manifests),
      displayGroups: this.reduceDisplayGroups(manifests),
      displayName: this.reduceDisplayName(manifests),
      // TODO: merge? only for children
      behaviors:
        (manifests[0].store.manifest?.editorElement as any)?.behaviors ?? {},
    });

    return {
      manifest: reducedManifest,
      mappings,
    };
  }

  private buildElements(tree: IVirtualManifest['tree']) {
    const elements: Map<string, IVirtualManifest['innerElementsIndexes'][0]> =
      new Map();

    const processElementsMap = (
      child: IVirtualManifest['tree'],
      breadcrumbs: string[],
      path: number[],
    ) => {
      child.children.forEach((_child, index) => {
        const rootEditorPointer = this.editorPointerAPI.buildEditorPointer(
          this.editorPointerAPI.getDocumentPointer(_child.editorPointer!),
        );

        if (!elements.has(rootEditorPointer.asUniqueKey)) {
          elements.set(rootEditorPointer.asUniqueKey, {
            rootEditorPointer,
            elements: {},
          });
        }

        const item = elements.get(rootEditorPointer.asUniqueKey)!;

        elements.set(rootEditorPointer.asUniqueKey, {
          ...item,
          elements: {
            ...item.elements,
            [[...breadcrumbs, _child.elementName!].join('.')]: [...path, index],
          },
        });

        processElementsMap(
          _child,
          [...breadcrumbs, _child.elementName!],
          [...path, index],
        );
      });
    };

    processElementsMap(tree, [], []);

    return [...elements.values()];
  }

  public build(manifestId: string): IVirtualManifest {
    const context = this.#mergeVirtualManifests(
      this.state.manifests.length
        ? this.reduceManifests(this.state.manifests)
        : null,
      this.state.virtualManifests,
    );

    const children = [
      ...this.state.manifests
        .map((x) => this.buildChildren(x, x.store.componentType, ['elements']))
        .flat(),
      ...this.state.virtualManifests.map((x) => x.tree.children).flat(),
    ];

    const tree = {
      elementType: VirtualManifestElementType.Root,
      breadcrumbs: [],
      editorPointer: null,
      extensionType: null,
      elementName: null,
      componentType: (this.state.manifests.length > 1
        ? null
        : this.state.manifests[0]?.store.componentType)!,
      manifest: {
        editorElement: context.manifest,
      },
      mappings: context.mappings,
      children,
    };

    return {
      virtualManifestId: manifestId,
      childPath: [],
      innerElementsIndexes: this.buildElements(tree),
      tree,
      manifests: [
        ...this.state.manifests.map((x) => {
          return {
            editorPointer: x.editorPointer,
            store: x.store,
          };
        }),
        ...this.state.virtualManifests
          .map((x) => {
            return x.manifests;
          })
          .flat(),
      ],
      overriddenDependencies: this.contextAPIContributor.getTokens(),
    };
  }
}

function filterUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined),
  ) as Partial<T>;
}

function getUniformValue<T, K extends keyof T, F extends T[K]>(
  items: T[],
  key: K,
  fallback: F,
): T[K] | F | undefined {
  const values = items.map((x) => x[key]);

  if (values.every((v) => v === undefined)) {
    return undefined;
  }

  const firstDefined = values.find((v) => v !== undefined);
  if (values.every((v) => v === firstDefined)) {
    return firstDefined;
  }

  return fallback;
}

function getOrderedElementNames(
  elements: NonNullable<EditorElement['elements']>,
  displayGroups?: EditorElement['displayGroups'],
) {
  const elementNames = Object.keys(elements);

  if (!elementNames.length || !displayGroups) {
    return elementNames;
  }

  const elementsDisplayGroup = Object.values(displayGroups).find(
    (group) => group.groupType === DISPLAY_GROUPS.GROUP_TYPE.elements,
  );

  const orderedItems = elementsDisplayGroup?.elements?.items;

  if (!orderedItems?.length) {
    return elementNames;
  }

  const existingNames = new Set(elementNames);

  const addedNames = new Set<string>();
  const result: string[] = [];

  for (const name of orderedItems) {
    if (existingNames.has(name) && !addedNames.has(name)) {
      result.push(name);
      addedNames.add(name);
    }
  }

  for (const name of elementNames) {
    if (!addedNames.has(name)) {
      result.push(name);
    }
  }

  return result;
}
